import React, { useEffect, useState } from 'react';
import { ref, getBytes, uploadString, deleteObject } from 'firebase/storage';
import { Cable, FileJson, Upload, Trash2, RefreshCw, CheckCircle2, AlertTriangle, ExternalLink } from 'lucide-react';
import { storage } from '../firebase';
import { RUTA_DIAGRAMA, URL_VISOR_DIAGRAMA } from '../config/diagrama';

// Publicación del diagrama eléctrico que abre el visor.
//
// Acepta los dos formatos que produce Rocharamas:
//   · el generador "Diagramas eléctricos" (HTML): Archivo → Exportar .json
//     → { format: "diagrama-electrico/1", name, diagram: { devices, wires, junctions, groups } }
//   · el editor React (diagrama-rocharamas): Proyecto → Exportar
//     → { version, comps, wires }
// Se guarda tal cual en Storage (RUTA_DIAGRAMA) con un bloque extra
// `publicacion: { nombre, notas, publicadoEn, archivo }` que el visor muestra
// arriba. Lectura pública, escritura solo del administrador (ver storage.rules).

const TAMANO_MAXIMO = 5 * 1024 * 1024;

// Revisa que el archivo sea un diagrama de alguno de los dos formatos y cuenta lo que trae
const validarProyecto = (datos) => {
  if (!datos || typeof datos !== 'object' || Array.isArray(datos)) {
    throw new Error('El archivo no es un diagrama eléctrico.');
  }
  // Generador "Diagramas eléctricos": los datos vienen dentro de `diagram`
  const gen = datos.diagram && typeof datos.diagram === 'object' ? datos.diagram : datos;
  if (Array.isArray(gen.devices)) {
    if (!Array.isArray(gen.wires)) throw new Error('Al diagrama le faltan los cables. Vuelve a exportarlo desde el generador (Archivo → Exportar .json).');
    const sinTipo = gen.devices.filter((d) => !d || !d.id || !d.type).length;
    if (sinTipo) throw new Error(`${sinTipo} aparato(s) no tienen id o tipo. El archivo parece dañado.`);
    if (!gen.devices.length) throw new Error('El diagrama está vacío.');
    return {
      formato: 'generador',
      nombre: typeof datos.name === 'string' ? datos.name : '',
      componentes: gen.devices.length,
      cables: gen.wires.length,
      grupos: Array.isArray(gen.groups) ? gen.groups.length : 0,
    };
  }
  // Editor React
  if (Array.isArray(datos.comps) && Array.isArray(datos.wires)) {
    const sinTipo = datos.comps.filter((c) => !c || !c.id || !c.type).length;
    if (sinTipo) throw new Error(`${sinTipo} componente(s) no tienen id o tipo. El archivo parece dañado.`);
    if (!datos.comps.length) throw new Error('El diagrama está vacío.');
    const grupos = new Set(datos.wires.map((w) => w?.grupo || '').filter(Boolean));
    return { formato: 'editor', nombre: '', componentes: datos.comps.length, cables: datos.wires.length, grupos: grupos.size };
  }
  throw new Error('No se reconoce el diagrama. Expórtalo como .json desde el generador de diagramas (Archivo → Exportar .json).');
};

const formatearFecha = (iso) => {
  try {
    return new Date(iso).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso || '';
  }
};

const AdminDiagrama = () => {
  const [actual, setActual] = useState(null); // { publicacion, resumen }
  const [avisoActual, setAvisoActual] = useState('');
  const [cargando, setCargando] = useState(true);

  const [archivo, setArchivo] = useState(null); // { nombreArchivo, datos, resumen }
  const [nombre, setNombre] = useState('');
  const [notas, setNotas] = useState('');
  const [subiendo, setSubiendo] = useState(false);
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');

  const cargar = async () => {
    setCargando(true);
    setAvisoActual('');
    try {
      const bytes = await getBytes(ref(storage, RUTA_DIAGRAMA));
      const datos = JSON.parse(new TextDecoder().decode(bytes));
      setActual({ publicacion: datos.publicacion || {}, resumen: validarProyecto(datos) });
    } catch (err) {
      setActual(null);
      if (err?.code === 'storage/object-not-found') {
        setAvisoActual('Todavía no hay ningún diagrama publicado. El visor mostrará un aviso hasta que subas uno.');
      } else if (err?.code === 'storage/unauthorized') {
        setAvisoActual('Storage no deja leer diagramas/. Despliega las reglas actualizadas (firebase deploy --only storage).');
      } else {
        setAvisoActual(`No se pudo leer el diagrama publicado: ${err.message}`);
      }
    }
    setCargando(false);
  };

  useEffect(() => {
    cargar();
  }, []);

  const elegirArchivo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    setError('');
    setOk('');
    setArchivo(null);
    if (!file) return;
    try {
      if (file.size > TAMANO_MAXIMO) throw new Error('El archivo pasa de 5 MB. Un diagrama normal pesa unos cuantos KB.');
      let datos;
      try {
        datos = JSON.parse(await file.text());
      } catch {
        throw new Error('El archivo no es un JSON válido.');
      }
      const resumen = validarProyecto(datos);
      setArchivo({ nombreArchivo: file.name, datos, resumen });
      // El generador guarda el nombre del diagrama dentro del archivo
      if (!nombre.trim()) setNombre(resumen.nombre || file.name.replace(/\.json$/i, ''));
    } catch (err) {
      setError(err.message);
    }
  };

  const publicar = async () => {
    if (!archivo) return;
    const titulo = nombre.trim();
    if (!titulo) {
      setError('Ponle un nombre al diagrama; es lo que verá la gente arriba del visor.');
      return;
    }
    if (actual && !window.confirm('Ya hay un diagrama publicado. ¿Reemplazarlo por este?')) return;
    setError('');
    setOk('');
    setSubiendo(true);
    try {
      const publicado = {
        ...archivo.datos,
        publicacion: {
          nombre: titulo,
          notas: notas.trim(),
          publicadoEn: new Date().toISOString(),
          archivo: archivo.nombreArchivo,
        },
      };
      await uploadString(ref(storage, RUTA_DIAGRAMA), JSON.stringify(publicado), 'raw', {
        contentType: 'application/json',
        // Sin caché: al publicar una versión nueva el visor la ve al recargar
        cacheControl: 'no-cache, max-age=0',
      });
      setOk(`"${titulo}" publicado. El visor ya lo abre por defecto.`);
      setArchivo(null);
      setNombre('');
      setNotas('');
      await cargar();
    } catch (err) {
      console.error('Error publicando el diagrama', err);
      setError(
        err?.code === 'storage/unauthorized'
          ? 'Storage rechazó la subida. Despliega las reglas nuevas (firebase deploy --only storage) y confirma que tu usuario es administrador.'
          : `Error publicando el diagrama: ${err.message}`
      );
    } finally {
      setSubiendo(false);
    }
  };

  const quitar = async () => {
    if (!window.confirm('¿Quitar el diagrama publicado? El visor mostrará un aviso de que no hay diagrama.')) return;
    setError('');
    setOk('');
    try {
      await deleteObject(ref(storage, RUTA_DIAGRAMA));
      setOk('Diagrama retirado del visor.');
      await cargar();
    } catch (err) {
      setError(`No se pudo quitar: ${err.message}`);
    }
  };

  const campo = 'w-full bg-slate-900/50 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500 disabled:opacity-50';

  return (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-3">
        <Cable size={16} className="text-cyan-400" />
        <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">Diagrama eléctrico del visor</span>
        {actual && (
          <span className="bg-green-500/20 text-green-400 text-[10px] px-2 py-0.5 rounded border border-green-500/30 font-black uppercase tracking-wider">
            publicado
          </span>
        )}
        {URL_VISOR_DIAGRAMA && (
          <a
            href={URL_VISOR_DIAGRAMA}
            target="_blank"
            rel="noopener noreferrer"
            className="ml-auto flex items-center gap-1 text-xs text-cyan-400 hover:text-cyan-300"
          >
            Abrir visor <ExternalLink size={12} />
          </a>
        )}
        <button
          onClick={cargar}
          className={`${URL_VISOR_DIAGRAMA ? '' : 'ml-auto '}p-1.5 rounded-lg bg-slate-700/50 text-slate-400 hover:text-white border border-slate-600`}
          title="Recargar"
        >
          <RefreshCw size={14} />
        </button>
      </div>

      <div className="bg-slate-700/30 rounded-xl border border-slate-600 p-4">
        {cargando ? (
          <div className="text-slate-500 text-sm">Cargando...</div>
        ) : (
          <>
            {actual ? (
              <div className="mb-4 flex items-start justify-between gap-3">
                <div className="text-sm min-w-0">
                  <div className="flex items-center gap-2 text-green-400 font-bold">
                    <CheckCircle2 size={16} className="shrink-0" />
                    <span className="truncate">{actual.publicacion.nombre || 'Diagrama sin nombre'}</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    {actual.resumen.componentes} componentes · {actual.resumen.cables} cables
                    {actual.resumen.grupos ? ` · ${actual.resumen.grupos} grupos` : ''}
                    {actual.publicacion.publicadoEn ? ` · publicado ${formatearFecha(actual.publicacion.publicadoEn)}` : ''}
                  </div>
                  {actual.publicacion.notas && <div className="text-xs text-slate-400 mt-1">{actual.publicacion.notas}</div>}
                </div>
                <button
                  onClick={quitar}
                  className="p-1.5 rounded bg-slate-700 text-red-400 hover:bg-red-500/20 border border-transparent hover:border-red-500/30 shrink-0"
                  title="Quitar el diagrama publicado"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ) : (
              <div className="mb-4 text-sm text-yellow-400 flex items-start gap-2">
                <AlertTriangle size={16} className="shrink-0 mt-0.5" /> {avisoActual}
              </div>
            )}

            {error && <div className="mb-3 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg p-2">{error}</div>}
            {ok && <div className="mb-3 text-sm text-green-400 bg-green-500/10 border border-green-500/20 rounded-lg p-2">{ok}</div>}

            <div className="border-t border-slate-600/40 pt-4">
              <div className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">
                {actual ? 'Reemplazar por otro diagrama' : 'Publicar diagrama'}
              </div>
              <p className="text-xs text-slate-500 mb-3">
                Sube el .json que exportas en el generador de diagramas (Archivo → Exportar .json). Se abre en el
                visor en cuanto lo publiques, sin que nadie tenga que buscar el archivo.
              </p>

              <input
                type="file"
                accept=".json,application/json"
                onChange={elegirArchivo}
                disabled={subiendo}
                className="w-full text-sm text-slate-400 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-bold file:bg-cyan-600 file:text-white hover:file:bg-cyan-500 file:cursor-pointer disabled:opacity-50"
              />

              {archivo && (
                <>
                  <div className="mt-3 flex items-center gap-3 bg-slate-900/40 border border-slate-600 rounded-lg p-2">
                    <FileJson size={16} className="text-cyan-400 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-white truncate">{archivo.nombreArchivo}</div>
                      <div className="text-xs text-slate-500">
                        {archivo.resumen.componentes} componentes · {archivo.resumen.cables} cables
                        {archivo.resumen.grupos ? ` · ${archivo.resumen.grupos} grupos` : ''}
                      </div>
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-3 mt-3">
                    <div>
                      <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">Nombre</label>
                      <input value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder="CNC Rocharamas" disabled={subiendo} className={campo} />
                    </div>
                    <div>
                      <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">Notas (opcional)</label>
                      <input value={notas} onChange={(e) => setNotas(e.target.value)} placeholder="Qué cambia en esta versión" disabled={subiendo} className={campo} />
                    </div>
                  </div>

                  <button
                    onClick={publicar}
                    disabled={subiendo}
                    className="mt-3 flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white rounded-lg font-bold text-sm disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {subiendo ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Upload size={16} />}
                    Publicar en el visor
                  </button>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminDiagrama;
